import { useEffect, useState } from 'react'
import { useNls } from '../../nls/nlsProvider'
import {
  verifyEduCertificate,
  type VerifyEduCertificateResult,
} from '../../services/eduProgressService'
import { eduServerErrorCode } from '../Education/classesBuilderHelpers'
import { isEduDemoWorkspace } from '../../services/eduDemo/eduDemoWorkspaces'
import { SectionLoader } from '../Common'
import './certificateVerifyPage.css'

export interface CertificateVerifyPageProps {
  code: string
  workspaceId?: string
}

type VerifyPageState =
  | { status: 'LOADING' }
  | { status: 'NOT_FOUND' }
  | { status: 'RATE_LIMITED' }
  | { status: 'ERROR' }
  | { status: 'SUCCESS'; data: VerifyEduCertificateResult }

export const CertificateVerifyPage = ({ code, workspaceId }: CertificateVerifyPageProps) => {
  const { t } = useNls()
  const cleanCode = (code || '').trim().toUpperCase()
  const isDemo = isEduDemoWorkspace(workspaceId)
  const pattern = isDemo ? /^[A-Z0-9-]{1,64}$/ : /^[A-Z2-7]{1,64}$/
  const isMalformed = !cleanCode || !pattern.test(cleanCode)

  const [state, setState] = useState<VerifyPageState>(() => {
    if (isMalformed) return { status: 'NOT_FOUND' }
    return { status: 'LOADING' }
  })

  useEffect(() => {
    document.title = t('NLSVerifyCertificateDocTitle')
  }, [t])

  useEffect(() => {
    if (isMalformed) return

    let active = true

    void (async () => {
      try {
        const result = await verifyEduCertificate({ code: cleanCode, workspaceId })
        if (!active) return
        setState({ status: 'SUCCESS', data: result })
      } catch (err: unknown) {
        if (!active) return
        const errCode = eduServerErrorCode(err)
        if (errCode === 'resource-exhausted' || errCode === 'functions/resource-exhausted') {
          setState({ status: 'RATE_LIMITED' })
        } else if (errCode === 'not-found' || errCode === 'functions/not-found') {
          setState({ status: 'NOT_FOUND' })
        } else {
          setState({ status: 'ERROR' })
        }
      }
    })()

    return () => {
      active = false
    }
  }, [cleanCode, isMalformed, workspaceId])

  const renderContent = () => {
    if (state.status === 'LOADING') {
      return (
        <div className="certificateVerifyPage__loading" data-testid="verify-loading">
          <SectionLoader />
          <span>{t('NLSVerifyCertificateLoading')}</span>
        </div>
      )
    }

    if (state.status === 'NOT_FOUND') {
      return (
        <div className="certificateVerifyPage__message certificateVerifyPage__message--muted" data-testid="verify-not-found">
          <p>{t('NLSVerifyCertificateNotFound')}</p>
          {cleanCode && (
            <p className="certificateVerifyPage__codeSnippet">
              {t('NLSVerifyCertificateCodeLabel', { code: cleanCode })}
            </p>
          )}
        </div>
      )
    }

    if (state.status === 'RATE_LIMITED') {
      return (
        <div className="certificateVerifyPage__message certificateVerifyPage__message--error" data-testid="verify-rate-limited">
          <p>{t('NLSVerifyCertificateRateLimited')}</p>
        </div>
      )
    }

    if (state.status === 'ERROR') {
      return (
        <div className="certificateVerifyPage__message certificateVerifyPage__message--error" data-testid="verify-error">
          <p>{t('NLSVerifyCertificateGenericError')}</p>
        </div>
      )
    }

    const { data } = state
    const statusLabel =
      data.status === 'VALID'
        ? t('NLSVerifyCertificateStatusValid')
        : data.status === 'EXPIRED'
          ? t('NLSVerifyCertificateStatusExpired')
          : data.status === 'REVOKED'
            ? t('NLSVerifyCertificateStatusRevoked')
            : t('NLSVerifyCertificateStatusReplaced')

    return (
      <div className="certificateVerifyPage__success" data-testid="verify-success">
        <div className="certificateVerifyPage__statusBadge" data-status={data.status}>
          <span className="certificateVerifyPage__statusText" data-status={data.status}>
            {statusLabel}
          </span>
        </div>

        {data.demoNotice && (
          <div className="certificateVerifyPage__demoNotice" data-testid="cert-demo-notice">
            {t('NLSVerifyCertificateDemoNotice') || data.demoNotice}
          </div>
        )}

        <div className="certificateVerifyPage__detailsGrid">
          <div className="certificateVerifyPage__detailRow">
            <span className="certificateVerifyPage__detailLabel">{t('NLSVerifyCertificateTitleLabel')}</span>
            <span className="certificateVerifyPage__detailValue" data-testid="cert-title">{data.title}</span>
          </div>

          <div className="certificateVerifyPage__detailRow">
            <span className="certificateVerifyPage__detailLabel">{t('NLSVerifyCertificateHolderLabel')}</span>
            <span className="certificateVerifyPage__detailValue" data-testid="cert-holder">{data.holder}</span>
          </div>

          <div className="certificateVerifyPage__detailRow">
            <span className="certificateVerifyPage__detailLabel">{t('NLSVerifyCertificateProgrammeLabel')}</span>
            <span className="certificateVerifyPage__detailValue" data-testid="cert-programme">{data.programme}</span>
          </div>

          <div className="certificateVerifyPage__detailRow">
            <span className="certificateVerifyPage__detailLabel">{t('NLSVerifyCertificateWorkspaceLabel')}</span>
            <span className="certificateVerifyPage__detailValue" data-testid="cert-workspace">{data.workspaceName}</span>
          </div>

          <div className="certificateVerifyPage__detailRow">
            <span className="certificateVerifyPage__detailLabel">{t('NLSVerifyCertificateIssuedLabel')}</span>
            <span className="certificateVerifyPage__detailValue" data-testid="cert-issued">{data.issueDate}</span>
          </div>

          <div className="certificateVerifyPage__detailRow">
            <span className="certificateVerifyPage__detailLabel">{t('NLSVerifyCertificateExpiresLabel')}</span>
            <span className="certificateVerifyPage__detailValue" data-testid="cert-expiry">
              {data.expiryDate || t('NLSVerifyCertificateNoExpiry')}
            </span>
          </div>

          <div className="certificateVerifyPage__detailRow">
            <span className="certificateVerifyPage__detailLabel">{t('NLSVerifyCertificateCodeLabel', { code: '' })}</span>
            <span className="certificateVerifyPage__detailValue certificateVerifyPage__codeSnippet" data-testid="cert-code">
              {cleanCode}
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="certificateVerifyPage" data-testid="certificate-verify-page">
      <div className="certificateVerifyPage__card">
        <header className="certificateVerifyPage__header">
          <h1 className="certificateVerifyPage__heading">
            {t('NLSVerifyCertificateHeading')}
          </h1>
        </header>

        {renderContent()}
      </div>
    </div>
  )
}

export default CertificateVerifyPage
